import debug from "debug";

const log = debug("app:csv");

/**
 * Class responsible for generating and downloading CSV files from BOM data.
 * @category Components
 * @subcategory BOM
 */
export class DownloadCsv {
  /**
   * Creates an instance of DownloadCsv.
   * Initializes CSV data and sets up event listeners for handling CSV generation and download.
   */
  constructor() {
    this.eventHub = experience.eventHub;
    this.csvData = "";
    this.setupEventListeners();
  }

  /**
   * Sets up event listeners for BOM data changes and download button clicks.
   * Listens for the `bomDataChanged` event to generate CSV data and attaches a click event to the download button.
   */
  setupEventListeners() {
    document.addEventListener("bomDataChanged", (event) => {
      log("bomDataChanged event received");
      this.generateCsvData(event.detail);
    });

    const downloadButton = document.getElementById("bomTable-download-button");
    if (downloadButton) {
      downloadButton.addEventListener("click", () => {
        this.downloadCSV();
      });
    }
  }

  cleanup() {
    console.log("DownloadCsv cleanup started...");
  
    // 1. Remove event listeners from document
    if (this.bomDataChangedHandler) {
      document.removeEventListener("bomDataChanged", this.bomDataChangedHandler);
    }
  
    if (this.downloadButton && this.downloadCsvHandler) {
      this.downloadButton.removeEventListener("click", this.downloadCsvHandler);
    }
  
    // 2. Clear object references
    this.csvData = null;
    this.downloadButton = null;
  
    console.log("DownloadCsv cleanup completed.");
  }

  /**
   * Generates CSV data from the provided BOM details.
   * The generated CSV includes headers, rows of data, and an estimated total row at the end.
   * 
   * @param {Object} detail - The BOM data used to generate the CSV.
   * @param {Object} detail.header - An object mapping column names to data keys.
   * @param {Array<Object>} detail.data - An array of rows representing BOM items.
   * @param {Array} detail.total - An array containing the total summary, value, and note.
   */
  generateCsvData(detail) {
    this.csvData = "";

    let headerKeys = Object.keys(detail.header);
    for (let i = 0; i < headerKeys.length; i++) {
      let delimiter = i + 1 === headerKeys.length ? "\n" : ",";
      this.csvData += headerKeys[i] + delimiter;
    }

    detail.data.forEach((row) => {
      headerKeys.forEach((key, index) => {
        let actualKey = detail.header[key];
        let value = row[actualKey];
        let delimiter = index + 1 === headerKeys.length ? "\n" : ",";
        this.csvData += `${value}${delimiter}`;
      });
    });

    // Add the estimated total row at the end
    const totalRow = `Estimated (USD),,,,${detail.total[1]},${detail.total[2]}\n`;
    this.csvData += totalRow;

    log("Generated CSV Data:", this.csvData);
  }

  /**
   * Initiates the download of the generated CSV file.
   * Alerts the user if no data is available for download.
   */
  downloadCSV() {
    if (!this.csvData) {
      alert("No data available for download.");
      return;
    }

    const blob = new Blob([this.csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "XMW-BOM.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
